import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { Trans } from 'react-i18next';
import {
  dateFormatter,
  multipleValueFormatterProps,
  numberValueFormatter,
} from '@app/shared/formatters';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import { useLocale } from '@app/core/hooks/useLocale';
import styles from './sponsor-irr-and-hurdle.module.scss';
import contentHeaderStyles from '@app/shared/layout/content-header/content-header.module.scss';
import SvgReturn from '@app/shared/icons/Return';
import {
  CalcMethod,
  SponsorIrrAndHurdleTab,
} from '@app/shared/models/contracts/enums/shared-enums';
import { useFocusMode } from '@app/shared/hooks/focus-mode';
import {
  IrrHurdleFocusSelections,
  SponsorIrrFocusSelections,
} from '@app/shared/hooks/focus-mode/focus-mode-types';
import { FC } from 'react';

const SponsorIRRAndHurdleFocusMode: FC = () => {
  const { l } = useLocale();
  const {
    isActive,
    isPaused,
    selections,
    pauseFocus: exitFocus,
    regainFocus,
    returnToPriorPage,
  } = useFocusMode();
  const project = useAppSelector((state) => state.project.projectDraft);
  const isOpmOnly = project.details.calcMethod === CalcMethod.OPM;
  const instrumentsMap =
    useAppSelector((state) => state.capitalStructure?.values?.instrumentInformation?.instruments) ??
    {};

  if (!isActive && !isPaused) {
    return null;
  }

  const focusCaseId = selections?.case;
  const caseNarrative = project?.pwermInput?.cases?.find(
    (item) => item.caseId === focusCaseId
  )?.narrative;

  let descriptionContent: React.ReactNode = null;

  if (selections) {
    const isSponsorIrr = selections.type === SponsorIrrAndHurdleTab.SponsorIRR;

    if (isSponsorIrr) {
      const sponsorSelections = selections as SponsorIrrFocusSelections;
      const multiple = project?.pwermInput?.cases
        .find((item) => item.caseId === focusCaseId)
        ?.multiples.find((item) => item.multipleId === sponsorSelections.multiple)?.multiple;

      descriptionContent = (
        <Trans
          i18nKey="_FocusCaseIrrYearMultiple"
          values={{
            case: caseNarrative,
            year: dateFormatter(sponsorSelections.year, { month: 'short', year: '2-digit' }),
            multiple: numberValueFormatter({
              ...multipleValueFormatterProps,
              value: Number(multiple),
            }),
          }}
        />
      );
    } else {
      const irrSelections = selections as IrrHurdleFocusSelections;
      const instrumentNarrative =
        irrSelections.instrument && instrumentsMap[irrSelections.instrument]?.narrative;
      const formattedHurdleTitle = irrSelections.hurdleTitle
        ? irrSelections.hurdleTitle.split(' ').slice(2).join(' ')
        : '';

      descriptionContent = (
        <Trans
          i18nKey={isOpmOnly ? '_FocusInstrumentHurdleYear' : '_FocusCaseInstrumentHurdleYear'}
          values={{
            case: caseNarrative,
            year: dateFormatter(irrSelections.year, { month: 'short', year: '2-digit' }),
            hurdle: formattedHurdleTitle,
            instrument: instrumentNarrative,
          }}
        />
      );
    }
  }

  const buttons = isActive ? (
    <>
      <Button
        appearance={ButtonAppearance.DEFAULT_SECONDARY}
        size={ButtonSize.SMALL}
        onClick={returnToPriorPage}
        startIcon={<SvgReturn />}>
        {l('_ReturnToPriorPage')}
      </Button>
      <Button
        appearance={ButtonAppearance.DEFAULT_SECONDARY}
        size={ButtonSize.SMALL}
        onClick={exitFocus}>
        {l('_ExitFocus')}
      </Button>
    </>
  ) : (
    <Button
      appearance={ButtonAppearance.DEFAULT_SECONDARY}
      size={ButtonSize.SMALL}
      onClick={regainFocus}>
      {l('_RegainFocus')}
    </Button>
  );

  return (
    <div className={contentHeaderStyles['content-header__focus-mode']}>
      <div className={styles['focus-mode-container']}>
        <div>{descriptionContent}</div>
        {buttons}
      </div>
    </div>
  );
};

export default SponsorIRRAndHurdleFocusMode;
